// Types qui reprennent exactement les tables et les enums de schema.sql.

export type UserRole = 'proprietaire' | 'gerant' | 'assistant'
export type UnitKind = 'commercial' | 'habitation'
export type LeaseStatus = 'actif' | 'expire' | 'termine'
export type PaymentMethod = 'especes' | 'wave' | 'virement' | 'cheque'
export type ExpenseStatus = 'en_attente' | 'approuve' | 'refuse'
export type DepositKind = 'caution' | 'mois_avance' | 'frais_agence'
export type DepositStatus = 'detenu' | 'rembourse' | 'applique' | 'introuvable'
export type Holder = 'proprietaire' | 'gerant' | 'mere' | 'banque' | 'autre'
export type Payer =
  | 'proprietaire' | 'gerant' | 'assistant' | 'mere' | 'locataire' | 'autre'

export type ExpenseCategory =
  | 'plomberie' | 'vidange' | 'eau' | 'electricite'
  | 'peinture' | 'amende' | 'menage' | 'autre'

export type DocKind =
  | 'contrat' | 'quittance' | 'recu' | 'facture' | 'bordereau'
  | 'etat_des_lieux' | 'piece_identite' | 'photo' | 'autre'

export interface Profile {
  id: string
  full_name: string | null
  role: UserRole | null
  lang: 'fr' | 'en'
}

export interface Unit {
  id: string
  name: string
  kind: UnitKind
  floor: string | null
  notes: string | null
  vacant_since: string | null
  vacancy_reason: string | null
}

export interface Tenant {
  id: string
  full_name: string
  phone: string | null
  id_type: string | null
  id_number: string | null
  notes: string | null
}

export interface Lease {
  id: string
  unit_id: string
  tenant_id: string
  rent_ht: number
  tva_rate: number
  rent_ttc: number
  deposit_amount: number
  advance_month: number
  agency_fee: number
  start_date: string | null
  end_date: string | null
  status: LeaseStatus
  contract_document: string | null
  supersedes_lease_id: string | null
  change_reason: string | null
  a_verifier: boolean
  verifier_note: string | null
}

/** Un bail accompagne du nom de son locataire et de son unite. */
export interface LeaseWithNames extends Lease {
  tenant?: { full_name: string } | null
  unit?: { name: string; kind: UnitKind } | null
}

export interface RentCharge {
  id: string
  lease_id: string
  period: string
  amount_due: number
  covered_by_advance: boolean
}

export interface Payment {
  id: string
  charge_id: string
  period: string
  paid_on: string
  amount: number
  method: PaymentMethod
  receipt_number: string | null
  receipt_document: string | null
  notes: string | null
}

export interface Expense {
  id: string
  spent_on: string
  period: string
  category: ExpenseCategory
  unit_id: string | null
  supplier: string | null
  amount: number
  paid_by: Payer
  description: string | null
  invoice_document: string | null
  status: ExpenseStatus
  approved_by: string | null
  approved_at: string | null
  decision_reason: string | null
  tenant_charge: boolean
}

export interface Deposit {
  id: string
  lease_id: string
  kind: DepositKind
  amount: number
  received_on: string | null
  held_by: Holder
  held_by_note: string | null
  proof_document: string | null
  status: DepositStatus
  notes: string | null
}

/** Etat d'un loyer pour un mois, tel que le calcule la vue SQL. */
export interface RentStatusRow {
  charge_id: string
  period: string
  lease_id: string
  unit_id: string
  unit_name: string
  tenant_id: string
  tenant_name: string
  amount_due: number
  covered_by_advance: boolean
  amount_received: number
  balance: number
  status: 'paye' | 'partiel' | 'impaye'
  first_payment_on: string | null
  last_payment_on: string | null
  due_on: string
  days_late: number | null
}

// ---------------------------------------------------------------------
// Permissions. Elles refletent ce que la base impose deja : ces
// fonctions evitent d'afficher un bouton qui serait de toute facon
// refuse, elles ne constituent pas la securite.
// ---------------------------------------------------------------------

/** Seul le proprietaire approuve, cloture et supprime. */
export function canApprove(role: UserRole | null): boolean {
  return role === 'proprietaire'
}

export function canEnterData(role: UserRole | null): boolean {
  return role === 'proprietaire' || role === 'gerant' || role === 'assistant'
}

export function canDelete(role: UserRole | null): boolean {
  return role === 'proprietaire'
}
